import React, { useMemo, useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import { LineItem } from 'shared/types/cart';
import Message from 'components/commercetools-ui/atoms/message';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart } from 'frontastic';
import { ImageProps } from 'frontastic/lib/image';
import CartItem from './cart-item';

interface Props {
  cartItems: LineItem[];
  isStockChecked?: boolean;
  errorId: string;
  productLogo?: ImageProps;
}

const CartItemsList: React.FC<Props> = ({ cartItems: initialCartItems, isStockChecked, errorId, productLogo }) => {
  const { taxCategoryRate, data, hasExcludedItem, hasOnlineGiftcard, hasItemsBelowMinQty, removeItem, updateItem } =
    useCart();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { getCountry } = useLocale();
  const country = data?.shippingAddress?.country;
  const site = country ? (country === 'GB' ? 'UK' : 'EU') : getCountry() === 'GB' ? 'UK' : 'EU';
  const [hasInsufficientStock, setHasInsufficientStock] = useState(false);
  const [cookies, _] = useCookies(['locale_selection']);
  const countryCookie = cookies?.locale_selection ? cookies?.locale_selection['countryCode'] : 'GB';
  const isCountryCodePresent = (countryCode: string) => {
    return countryList.some((country) => country.countryCode === countryCode);
  };
  const isCountrySupported = isCountryCodePresent(countryCookie);
  const [cartItems, setCartItems] = useState(initialCartItems);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setCartItems(initialCartItems);
  }, [initialCartItems]);

  // Check for insufficient stock in all cart items
  useEffect(() => {
    const insufficient = cartItems?.some(
      (item) =>
        typeof item?.count === 'number' &&
        typeof item?.fluentAvailableQuantity === 'number' &&
        item?.isCustomFit === false &&
        item?.count > item?.fluentAvailableQuantity,
    );
    setHasInsufficientStock(insufficient);
  }, [cartItems]);

  const taxPortionName = useMemo(() => {
    const taxPortionsArray = data?.taxed?.taxPortions ?? [];
    return taxPortionsArray.length > 0 ? taxPortionsArray[0].name || '' : '';
  }, [data?.taxed]);

  const availableItems = useMemo(() => {
    return cartItems.filter((item) => {
      return item.isPerpetualStock || item.isOnStock;
    });
  }, [cartItems, site]);

  const soldOutItems = useMemo(() => {
    return cartItems.filter(
      (item) =>
        !item.isPerpetualStock &&
        !item.isOnStock &&
        !item.exclusions?.some((exclusion) => exclusion.toLowerCase() === site.toLowerCase()),
    );
  }, [cartItems, site]);

  // Optimistic remove handler
  const handleRemove = async (lineItemId: string) => {
    const prevItems = cartItems;
    setRemovingId(lineItemId);
    setCartItems((items) => items?.filter((item) => item?.lineItemId !== lineItemId));
    try {
      await removeItem(lineItemId);
    } catch (error) {
      setCartItems(prevItems);
      alert('Failed to remove item. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  // Optimistic quantity update handler
  const handleUpdateQuantity = async (lineItemId: string, newQuantity: number) => {
    const prevItems = cartItems;
    setUpdatingId(lineItemId);
    setCartItems((items) =>
      items?.map((item) =>
        item?.lineItemId === lineItemId ? { ...item, count: newQuantity == 0 ? 1 : newQuantity } : item,
      ),
    );
    try {
      await updateItem(lineItemId, newQuantity);
      setUpdatingId(null);
    } catch (error) {
      setCartItems(prevItems);
      setUpdatingId(null);
      alert('Failed to update quantity. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    const shouldShow = (isStockChecked && (soldOutItems?.length > 0 || hasInsufficientStock)) || errorId === 'error04';
    const timer = setTimeout(() => setShowError(shouldShow), 200);
    return () => clearTimeout(timer);
  }, [isStockChecked, soldOutItems, hasInsufficientStock, errorId]);

  return (
    <div className="mt-12 divide-y divide-neutral-400 border-t border-neutral-400 lg:mt-34 lg:border-none">
      {showError && (
        <Message
          severity={'error'}
          message={formatCartMessage({
            id: 'sold.out.error',
            defaultMessage:
              "Unfortunately, your cart includes items which can't be fulfilled.  Please remove these items from your cart to continue.",
          })}
        />
      )}

      {!isCountrySupported && (
        <Message
          severity={'error'}
          message={formatCartMessage({
            id: 'shipping.error',
            defaultMessage:
              "Unfortunately, your cart can't be fulfilled to your chosen country at this time.  Please change your delivery country to continue.",
          })}
        ></Message>
      )}

      {hasOnlineGiftcard && (
        <Message
          severity={'info'}
          message={formatCartMessage({
            id: 'cart.giftcart.delivery.message',
            defaultMessage: 'E Gift Cards will be delivered via email within 48 hours',
          })}
        ></Message>
      )}

      {hasExcludedItem && (
        <Message
          severity={'error'}
          message={formatCartMessage({
            id: 'shipping.error',
            defaultMessage:
              "Unfortunately, your cart includes items which can't be fulfilled to your chosen location. Please remove these items from your cart to continue.",
          })}
        ></Message>
      )}

      {hasItemsBelowMinQty && (
        <Message
          severity={'error'}
          message={formatCartMessage({
            id: 'item.min.qty.not.met',
            defaultMessage:
              'Unfortunately, your cart includes items requiring a minimum order quantity.  Please update minimum order quantity to cart to continue.',
          })}
        ></Message>
      )}

      {(isStockChecked ? availableItems : cartItems)?.map((item) => {
        return (
          <div key={item?.lineItemId}>
            <CartItem
              taxPortionName={taxPortionName}
              taxCategoryRate={taxCategoryRate.data}
              item={item}
              productLogo={productLogo}
              classNames={{ moveToWishlist: 'text-14' }}
              isOnStock={item?.isOnStock}
              isDiscontinued={item?.variant?.attributes?.isDiscontinued}
              fluentAvailableQuantity={item?.fluentAvailableQuantity}
              isGiftcard={item.isGiftcard ?? false}
              isStockChecked={isStockChecked}
              isExcluded={item?.exclusions?.some((exclusion) => exclusion.toLowerCase() === site.toLowerCase())}
              onRemove={handleRemove}
              removing={removingId === item?.lineItemId}
              onUpdateQuantity={handleUpdateQuantity}
              updating={updatingId === item?.lineItemId}
            />
          </div>
        );
      })}
      {/* sold out items */}
      {isStockChecked && soldOutItems?.length > 0 && (
        <div className="border-t border-neutral-400 pt-36">
          {soldOutItems?.map((item) => (
            <div className="mt-52" key={item?.lineItemId}>
              <CartItem
                item={item}
                classNames={{ moveToWishlist: 'text-14' }}
                productLogo={productLogo}
                isOnStock={false}
                isStockChecked={isStockChecked}
                fluentAvailableQuantity={item?.fluentAvailableQuantity}
                isDiscontinued={item?.variant?.attributes?.isDiscontinued}
                isSold={true}
                onRemove={handleRemove}
                removing={removingId === item?.lineItemId}
                onUpdateQuantity={handleUpdateQuantity}
                updating={updatingId === item?.lineItemId}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CartItemsList;
